import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, map, of, switchMap, tap } from 'rxjs';
import {
  AuthenticationService,
  KeywordService,
  Project,
  ProjectService,
  TextureCategory,
  TextureService,
} from '../../openapi';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';

@Injectable({ providedIn: 'root' })
export class ModelViewerUrbaseeService {
  // JWT utilisé pour les requetes
  private jwt?: string;

  private project?: Project;
  private textures?: TextureCategory[];

  private projectSubject: BehaviorSubject<Project> =
    new BehaviorSubject<Project>({});
  public project$: Observable<Project> = this.projectSubject.asObservable();

  constructor(
    private httpClient: HttpClient,
    private authentificationService: AuthenticationService,
    private projectService: ProjectService,
    private textureService: TextureService,
    private keywordService: KeywordService
  ) {}

  /**
   * Return jwt
   */
  public getJwt(): string | undefined {
    return this.jwt;
  }

  /**
   * Fetch the jwt
   * @param token
   * @param applicationTypeCode
   * @param noCache
   * @returns
   */
  fetchJwt(
    token?: string,
    applicationTypeCode?: string,
    noCache: boolean = false
  ) {
    // Use cache
    if (this.jwt && !noCache) {
      return of(this.jwt);
    }
    return of(true).pipe(
      // Récupérer le code de login
      switchMap(() => {
        if (token) {
          // Un token existe
          return of(token);
        } else if (applicationTypeCode) {
          console.log(
            `[Model-Viewer-Urbasee] Application type: ${applicationTypeCode}`
          );
          // Aucun token existe, on tente d'en récupérer un
          return this.fetchAccountToken(applicationTypeCode);
        } else {
          throw new Error(`No token or application type code provided`);
        }
      }),
      tap((code) => {
        console.debug(`[Model-Viewer-Urbasee] Code: ${code}`);
      }),
      // Récupérer le jwt
      switchMap((code) => {
        return this.authentificationService.servicesV40AuthKeyPost(code).pipe(
          map((jwtObject) => {
            if (jwtObject.token) {
              return jwtObject.token;
            } else {
              throw new Error(`Fail to fetch jwt`);
            }
          })
        );
      }),
      tap((jwt?: string) => {
        if (jwt) {
          console.debug(
            `[Model-Viewer-Urbasee] Retreived jwt: ${jwt.substring(0, 10)}...`
          );
          this.jwt = jwt;
        } else {
          throw new Error(`Fail to fetch jwt`);
        }
      })
    );
  }

  // Do request for fetch model file
  public fetchProject(projectId: number, noCache = false): Observable<Project> {
    // Use cache
    if (this.project && !noCache) {
      return of(this.project);
    }
    return this.projectService.servicesV40ProjectProjectIdGet(
      '' + projectId,
      undefined,
      undefined,
      {}
    );
  }

  // Do request for fetch model file
  public fetchModel3dBlob(projectId: number): Observable<Blob> {
    return this.fetchProject(projectId).pipe(
      // Déclanche la récupération des couleurs
      map((project: any) => {
        if (project?.documents[0]?.id) {
          return `${environment.apiUrl}/services/v4.0/document/${project.documents[0].id}/download`;
        } else {
          throw new Error(
            `Fail to fetch project bundle for project ${projectId}`
          );
        }
      }),
      // Récupérer le fichier en blob
      switchMap((url) => {
        return this.httpClient.get(url, { responseType: 'blob' }).pipe(
          tap((blob) => {
            console.log(`[Model-Viewer-Urbasee] Retrieved model3d blob `, blob);
          })
        );
      })
    );
  }

  /**
   * Fetch all textureCategories with textureColors
   * @param project
   */
  fetchTextures(
    applicationId: number,
    noCache = false
  ): Observable<TextureCategory[]> {
    // Use cache
    if (this.textures && !noCache) {
      return of(this.textures);
    }
    return this.textureService
      .servicesV40ApplicationApplicationIdTextureCategoryGet('' + applicationId)
      .pipe(
        tap((textureCategories) => {
          this.textures = textureCategories;
          return textureCategories;
        })
      );
  }

  fetchKeywords(applicationId: number): Observable<any> {
    return this.keywordService
      .servicesV40ApplicationApplicationIdKeywordGet('' + applicationId)
      .pipe(
        tap((keywords) => {
          console.log(`[Model-Viewer-Urbasee] Keywords: `, keywords);
        })
      );
  }

  /**
   * Download anonymous code for login
   * @param applicationTypeCode
   * @returns
   */
  private fetchAccountToken(applicationTypeCode: string): Observable<string> {
    return this.authentificationService
      .servicesV40ApplicationTypeApplicationTypeCodeAnonymousTokenGet(
        applicationTypeCode
      )
      .pipe(
        map((tokenObject) => {
          if (tokenObject.code) {
            return tokenObject.code;
          } else {
            throw new Error(`Fail to fetch code for ${applicationTypeCode}`);
          }
        })
      );
  }
}
